import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useClaimStatuses } from '@/hooks/use-statuses';
import type { Claim, ClaimStatusTemplate } from '@/types/claims';

interface ClaimRowStatusSelectProps {
    claim: Pick<Claim, 'id' | 'companyId' | 'pipelineId' | 'claimStatusTemplateId' | 'claimStatusName' | 'claimStatusColor' | 'canWrite'>;
    statuses: Pick<ClaimStatusTemplate, 'id' | 'pipelineId' | 'displayName' | 'color'>[];
    viewerCompanyId: number | null;
    onChange: (statusTemplateId: number) => void;
    className: string;
}

export function ClaimRowStatusSelect({ claim, statuses, viewerCompanyId, onChange, className }: ClaimRowStatusSelectProps) {
    const isSharedClaim = viewerCompanyId != null && claim.companyId !== viewerCompanyId;
    if (isSharedClaim && claim.canWrite) {
        return <SharedClaimStatusSelect claim={claim} onChange={onChange} className={className} />;
    }

    const claimStatuses = statuses.filter((status) => status.pipelineId === claim.pipelineId);
    return (
        <ClaimStatusControl
            claim={claim}
            statuses={claimStatuses}
            onChange={onChange}
            className={className}
        />
    );
}

function SharedClaimStatusSelect({
    claim,
    onChange,
    className,
}: Pick<ClaimRowStatusSelectProps, 'claim' | 'onChange' | 'className'>) {
    const [isOpen, setIsOpen] = useState(false);
    const { statuses, isLoading } = useClaimStatuses(claim.id, claim.pipelineId, isOpen);
    return (
        <ClaimStatusControl
            claim={claim}
            statuses={statuses}
            isLoading={isLoading}
            onOpenChange={setIsOpen}
            onChange={onChange}
            className={className}
        />
    );
}

function ClaimStatusControl({
    claim,
    statuses,
    isLoading = false,
    onOpenChange,
    onChange,
    className,
}: Pick<ClaimRowStatusSelectProps, 'claim' | 'onChange' | 'className'> & {
    statuses: Pick<ClaimStatusTemplate, 'id' | 'displayName' | 'color'>[];
    isLoading?: boolean;
    onOpenChange?: (open: boolean) => void;
}) {
    const currentStatus = statuses.find((status) => status.id === claim.claimStatusTemplateId);
    const statusName = currentStatus?.displayName ?? claim.claimStatusName ?? null;
    const statusColor = currentStatus?.color ?? claim.claimStatusColor ?? '#94A3B8';

    if (!claim.canWrite) {
        return (
            <Badge variant="outline" className={className} style={{ borderColor: statusColor }}>
                {statusName ?? 'No Status'}
            </Badge>
        );
    }

    return (
        <Select
            value={claim.claimStatusTemplateId?.toString()}
            onValueChange={(templateId) => onChange(parseInt(templateId, 10))}
            onOpenChange={onOpenChange}
        >
            <SelectTrigger className={className}>
                <SelectValue>
                    {statusName ? (
                        <span className="inline-flex min-w-0 items-center gap-1.5">
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full border" style={{ backgroundColor: statusColor }} />
                            <span className="truncate">{statusName}</span>
                        </span>
                    ) : (
                        <span className="text-muted-foreground">No Status</span>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {isLoading && statuses.length === 0 ? (
                    <SelectItem value="__loading" disabled>Loading statuses...</SelectItem>
                ) : statuses.length > 0 ? (
                    statuses.map((status) => (
                        <SelectItem key={status.id} value={status.id.toString()}>
                            <span className="inline-flex items-center gap-2">
                                <span className="h-2.5 w-2.5 rounded-full border" style={{ backgroundColor: status.color ?? '#94A3B8' }} />
                                {status.displayName}
                            </span>
                        </SelectItem>
                    ))
                ) : (
                    <SelectItem value="__empty" disabled>No active statuses</SelectItem>
                )}
            </SelectContent>
        </Select>
    );
}